import type {
  SmartCreationBrowserDraft,
  SmartCreationStructure,
  SmartCreationStructureOptionsEvaluation,
  SmartCreationStructureRepairAction,
} from "@adpilot/shared";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { StrategyContextSummary } from "@/components/smart-creation/StrategyContextSummary";
import { getEmptySmartCreationStrategyLanes } from "@/components/smart-creation/StrategyStep";
import { StructureConceptDiagram } from "@/components/smart-creation/StructureConceptDiagram";
import {
  StructureTopology,
  StructureTopologyLegend,
} from "@/components/smart-creation/StructureTopology";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useIsVendorShop } from "@/hooks/use-shop-account-type";
import { cn } from "@/lib/utils";
import { distinctParentCount, formatCurrencyValue, structureEvaluation } from "./draft-rules";

export function StructureStep({
  plan,
  structure,
  evaluation,
  mutationPending,
  loading,
  error,
  onRetry,
  onStructureChange,
  onRepair,
}: {
  plan: SmartCreationBrowserDraft;
  structure: SmartCreationStructure;
  evaluation?: SmartCreationStructureOptionsEvaluation;
  mutationPending: boolean;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onStructureChange: (structure: SmartCreationStructure) => void;
  onRepair: (repair: SmartCreationStructureRepairAction) => void;
}) {
  const { t } = useTranslation("smartCreation");
  const reasonId = useId();
  const vendor = useIsVendorShop(plan.shopId);
  const structures = ["precise", "standard", "binary"] as const;
  const selectedEvaluation = structureEvaluation(evaluation, structure);
  const precise = structureEvaluation(evaluation, "precise");
  const preciseReason =
    distinctParentCount(plan.draft) > 1
      ? t("structure.preciseSingleParent")
      : !precise
        ? t("structure.preciseUnavailable")
        : precise.diagnostics
            .filter((item) => item.blocking)
            .map((item) => t(`structure.diagnostics.${item.code}`, { defaultValue: item.message }))
            .join(" ");
  const binaryOverridesOrganization =
    structure === "binary" &&
    plan.draft.productOrganization === "campaign" &&
    distinctParentCount(plan.draft) > 1;
  const recommendation = evaluation?.recommendation;
  const observedCpa =
    recommendation?.observedCpa && selectedEvaluation
      ? formatCurrencyValue(
          recommendation.observedCpa,
          selectedEvaluation.budget.currencyCode,
          selectedEvaluation.budget.decimals,
        )
      : null;
  return (
    <div className="flex flex-col gap-4">
      <StrategyContextSummary plan={plan} currencyCode={selectedEvaluation?.budget.currencyCode} />
      <section
        data-testid="structure-comparison"
        className="overflow-hidden rounded-md border border-line bg-surface"
        aria-label={t("structure.title")}
      >
        <div className="border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <h3 className="shrink-0 text-sm font-semibold">{t("structure.title")}</h3>
            <StructureTopologyLegend />
          </div>
          {getEmptySmartCreationStrategyLanes(plan.draft.strategy.branches).length > 0 ? (
            <p
              className="mt-1 text-xs text-muted-foreground"
              data-testid="structure-pending-targets"
            >
              {t("strategy.deferred.structurePending")}
            </p>
          ) : null}
        </div>
        <div className="grid grid-cols-1 divide-y divide-line md:grid-cols-3 md:divide-x md:divide-y-0">
          {structures.map((value) => {
            const option = structureEvaluation(evaluation, value);
            return (
              <div key={value} className="flex min-w-0 flex-col overflow-hidden">
                <h4 className="w-full px-3 pt-2.5 text-xs font-semibold">
                  {t(`structure.${value}`)}
                </h4>
                <StructureConceptDiagram structure={value} />
                <div className="w-full px-3 pb-3 text-center">
                  <p className="min-h-8 text-[11px] leading-4 text-muted-foreground">
                    {t(`structure.${value}Description`)}
                  </p>
                  <div
                    data-structure-metrics={value}
                    className="mt-2 flex justify-center gap-4 text-[11px] text-muted-foreground"
                  >
                    <span>
                      {t("structure.campaigns")}{" "}
                      <strong className="text-ink">{option?.counts.campaignCount ?? "—"}</strong>
                    </span>
                    <span>
                      {t("structure.adGroups")}{" "}
                      <strong className="text-ink">{option?.counts.adGroupCount ?? "—"}</strong>
                    </span>
                    <span>
                      {t("structure.minimum")}{" "}
                      <strong className="text-ink">
                        {option
                          ? formatCurrencyValue(
                              option.budget.minimumViableDailyBudget,
                              option.budget.currencyCode,
                              option.budget.decimals,
                            )
                          : "—"}
                      </strong>
                    </span>
                  </div>
                  <div className="mt-1 flex h-8 items-center justify-center">
                    {option?.draftContent ? (
                      <Popover>
                        <PopoverTrigger render={<Button type="button" variant="ghost" size="sm" />}>
                          {t("structure.actualTopology")}
                        </PopoverTrigger>
                        <PopoverContent className="max-h-[420px] w-[560px] max-w-[90vw] overflow-auto p-3">
                          <StructureTopology evaluation={option} vendor={vendor} />
                        </PopoverContent>
                      </Popover>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <ToggleGroup
        multiple={false}
        value={[structure]}
        onValueChange={(values) => {
          const value = values[0];
          if (
            value === "standard" ||
            value === "binary" ||
            (value === "precise" && !preciseReason && precise?.feasible)
          )
            onStructureChange(value);
        }}
        aria-label={t("structure.title")}
        className="grid w-full grid-cols-1 items-stretch gap-2 rounded-md bg-subtle p-2 md:grid-cols-3"
      >
        {structures.map((value) => {
          const selected = structure === value;
          const option = structureEvaluation(evaluation, value);
          return (
            <div key={value} className="flex min-w-0 items-center gap-1">
              <ToggleGroupItem
                value={value}
                disabled={
                  mutationPending ||
                  loading ||
                  !option ||
                  (value === "precise" && (!!preciseReason || !option.feasible))
                }
                aria-describedby={value === "precise" && preciseReason ? reasonId : undefined}
                data-structure-option={value}
                className={cn(
                  "h-10 w-full border border-line bg-surface font-medium hover:bg-subtle aria-pressed:border-primary aria-pressed:bg-primary/5",
                  selected && "text-ink",
                )}
              >
                <span
                  className={cn(
                    "grid size-4 place-items-center rounded-full border",
                    selected ? "border-primary" : "border-line",
                  )}
                  aria-hidden="true"
                >
                  {selected ? <span className="size-2 rounded-full bg-primary" /> : null}
                </span>
                {t(`structure.${value}`)}
                {recommendation?.suggested === value ? (
                  <Badge variant="info" size="compact">
                    {t("structure.recommended")}
                  </Badge>
                ) : null}
              </ToggleGroupItem>
            </div>
          );
        })}
      </ToggleGroup>
      <section
        data-testid="structure-selected-description"
        aria-live="polite"
        className="flex flex-col gap-1 px-1 text-xs leading-5 text-muted-foreground"
      >
        <p
          data-testid={binaryOverridesOrganization ? "binary-product-organization-note" : undefined}
        >
          {t(
            binaryOverridesOrganization
              ? "structure.binaryOverridesProductOrganization"
              : `structure.selectedDescriptions.${structure}`,
          )}
        </p>
        {recommendation && recommendation.basis !== "none" && selectedEvaluation ? (
          <p data-testid="structure-budget-guidance">
            {recommendation.basis === "platform_minimum"
              ? t("structure.guidance.platformMinimum", {
                  structure: t(`structure.${structure}`),
                  minimum: formatCurrencyValue(
                    selectedEvaluation.budget.minimumViableDailyBudget,
                    selectedEvaluation.budget.currencyCode,
                    selectedEvaluation.budget.decimals,
                  ),
                })
              : t(
                  selectedEvaluation.budget.allocations.length > 0 &&
                    selectedEvaluation.budget.allocations.every(
                      (allocation) =>
                        allocation.dailyBudget >= (recommendation.observedCpa ?? Infinity),
                    )
                    ? "structure.guidance.budgetMeets"
                    : "structure.guidance.budgetBelow",
                  {
                    structure: t(`structure.${structure}`),
                    days: recommendation.evidenceWindowDays,
                    amount: observedCpa,
                  },
                )}
          </p>
        ) : null}
        {preciseReason ? (
          <p id={reasonId} data-testid="precise-disabled-reason">
            {preciseReason}
          </p>
        ) : null}
        {selectedEvaluation?.diagnostics
          .filter((item) => !item.blocking)
          .map((item) => (
            <p key={`${item.code}:${item.path?.join(".") ?? "root"}`}>
              {t(`structure.diagnostics.${item.code}`, { defaultValue: item.message })}
            </p>
          ))}
        {selectedEvaluation && !selectedEvaluation.feasible ? (
          <div className="flex flex-col gap-1">
            <p className="font-medium text-destructive">{t("structure.blockedTitle")}</p>
            {selectedEvaluation.diagnostics
              .filter((item) => item.blocking)
              .map((item) => (
                <p
                  className="text-destructive"
                  key={`${item.code}:${item.path?.join(".") ?? "root"}`}
                >
                  {t(`structure.diagnostics.${item.code}`, { defaultValue: item.message })}
                </p>
              ))}
            <div className="flex flex-wrap gap-2">
              {selectedEvaluation.repairActions.map((repair) => (
                <Button
                  key={structureRepairKey(repair)}
                  variant="outline"
                  size="sm"
                  data-repair-action={repair.action}
                  disabled={mutationPending || loading}
                  onClick={() => onRepair(repair)}
                >
                  {structureRepairLabel(t, repair)}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
      </section>
      {error ? <ErrorState message={t("structure.loadErrorTitle")} onRetry={onRetry} /> : null}
    </div>
  );
}

function structureRepairKey(repair: SmartCreationStructureRepairAction): string {
  if (repair.action === "reduce_strategies") {
    return `${repair.action}:${repair.strategyKinds.join(",")}`;
  }
  return repair.action;
}

function structureRepairLabel(
  t: ReturnType<typeof useTranslation>["t"],
  repair: SmartCreationStructureRepairAction,
): string {
  if (repair.action === "increase_budget") {
    return t("structure.repairs.increaseBudget", {
      amount: formatCurrencyValue(repair.minimumTotalDailyBudget, repair.currencyCode),
    });
  }
  if (repair.action === "decrease_budget") {
    return t("structure.repairs.decreaseBudget", {
      amount: formatCurrencyValue(repair.maximumTotalDailyBudget, repair.currencyCode),
    });
  }
  if (repair.action === "switch_structure") return t("structure.repairs.switchBinary");
  if (repair.action === "reduce_strategies") {
    return t("structure.repairs.reduceStrategies", {
      strategies: repair.strategyKinds.map((kind) => t(`strategy.${kind}`)).join(", "),
    });
  }
  if (repair.action === "reduce_targets") {
    return t("structure.repairs.reduceTargets", {
      count: repair.maximumTargetsPerAdGroup,
    });
  }
  if (repair.action === "reduce_product_ads") {
    return t("structure.repairs.reduceProductAds", {
      count: repair.maximumProductsPerAdGroup,
    });
  }
  return t("structure.repairs.reduceProducts", {
    count: repair.maximumVariationGroups,
  });
}
